import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import calendarIcon from 'src/assets/calendar.svg';
// import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import './DatePicker.css';
import DatePicker, { DayValue } from 'react-modern-calendar-datepicker';
import { convertDateToDayValue, convertDayValueToDate } from 'src/utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './style.scoped.css';
dayjs.extend(customParseFormat);

interface InputDatePickerProps {
  label?: string;
  value: Date;
  helperText?: string;
  error?: boolean;
  onChange?: any;
  isRequired?: boolean;
  placeholder?: string;
  maxDate?: Date;
  onFocus?: () => void;
  onBlur?: () => void;
}

const vnLocale = {
  // months list by order
  months: [
    'Tháng 1',
    'Tháng 2',
    'Tháng 3',
    'Tháng 4',
    'Tháng 5',
    'Tháng 6',
    'Tháng 7',
    'Tháng 8',
    'Tháng 9',
    'Tháng 10',
    'Tháng 11',
    'Tháng 12',
  ],

  // week days by order
  weekDays: [
    {
      name: 'Thứ 2',
      short: 'T2',
    },
    {
      name: 'Thứ 3',
      short: 'T3',
    },
    {
      name: 'Thứ 4',
      short: 'T4',
    },
    {
      name: 'Thứ 5',
      short: 'T5',
    },
    {
      name: 'Thứ 6',
      short: 'T6',
    },
    {
      name: 'Thứ 7',
      short: 'T7',
      isWeekend: true,
    },
    {
      name: 'Chủ Nhật', // used for accessibility
      short: 'CN', // displayed at the top of days' rows
      isWeekend: true, // is it a formal weekend or not?
    },
  ],

  // just play around with this number between 0 and 6
  weekStartingIndex: 6,

  // return a { year: number, month: number, day: number } object
  getToday(gregorainTodayObject) {
    return gregorainTodayObject;
  },

  // return a native JavaScript date here
  toNativeDate(date) {
    return new Date(date.year, date.month - 1, date.day);
  },

  // return a number for date's month length
  getMonthLength(date) {
    return new Date(date.year, date.month, 0).getDate();
  },

  // return a transformed digit to your locale
  transformDigit(digit) {
    return digit;
  },

  // texts in the date picker
  nextMonth: 'Next Month',
  previousMonth: 'Previous Month',
  openMonthSelector: 'Open Month Selector',
  openYearSelector: 'Open Year Selector',
  closeMonthSelector: 'Close Month Selector',
  closeYearSelector: 'Close Year Selector',
  defaultPlaceholder: 'Select...',

  // for input range value
  from: 'from',
  to: 'to',

  // used for input value when multi dates are selected
  digitSeparator: ',',

  // if your provide -2 for example, year will be 2 digited
  yearLetterSkip: 0,

  // is your language rtl or ltr?
  isRtl: false,
};

const InputDatePicker = (props: InputDatePickerProps) => {
  const {
    label,
    value,
    helperText,
    error,
    onChange,
    isRequired = false,
    placeholder,
    maxDate,
    onFocus,
    onBlur,
    ...passProps
  } = props;
  const { t } = useTranslation();
  const _getInputBorderClassName = () => {
    if (error) return 'border-error';
    return 'border-line2';
    // return focus
    //   ? 'border-secondary ring-1 ring-secondary'
    //   : 'border-onSurface10';
  };

  const _handleChange = (newValue: DayValue) => {
    if (onChange) {
      onChange(convertDayValueToDate(newValue));
    }
  };

  const renderCustomInput = ({ ref }) => (
    <div className="flex flex-row items-center w-full py-3 pl-4 pr-3">
      <input
        readOnly
        ref={ref} // necessary
        value={value ? dayjs(value).format(t('date_format1')) : ''}
        className="focus:outline-none bg-surface flex-1 text-base font-semibold text-onSurface w-full"
        placeholder={placeholder}
        onFocus={() => {
          onFocus && onFocus();
        }}
        onBlur={() => {
          onBlur && onBlur();
        }}
      />
      <img
        src={calendarIcon}
        className="object-cover w-5 h-5"
        alt={'calendar'}
        style={{ position: 'absolute', right: 12, pointerEvents: 'none' }}
      />
    </div>
  );

  return (
    <div>
      <div className="labelText mb-2">
        {label}
        {isRequired ? <label className="bodyText text-error"> *</label> : ''}
      </div>
      <div
        className={`${_getInputBorderClassName()} flex flex-row items-center border rounded-md bg-surface`}
      >
        <DatePicker
          value={value ? convertDateToDayValue(value) : null}
          onChange={_handleChange}
          shouldHighlightWeekends
          colorPrimary={'#141ed2'}
          locale={vnLocale}
          renderInput={renderCustomInput}
          maximumDate={maxDate ? convertDateToDayValue(maxDate) : null}
          wrapperClassName={'datePicker'}
        />
      </div>
      {!!helperText && (
        <div
          className={`${
            error ? 'text-error' : 'text-labelHelper'
          } text-xs mt-2 font-normal`}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default InputDatePicker;
